import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
  Overlay,
} from '@angular/cdk/overlay';

type InfoTipPosition = 'above' | 'below' | 'left' | 'right';

/**
 * Small "i" affordance that reveals a long-form helper popover on hover or
 * keyboard focus. Built on CDK Overlay so positioning and styling are fully
 * under our control — unlike `matTooltip`, whose Material 3 design-token
 * surface and 200px-cap don't fit our section-header use case.
 *
 * The fallback positions (above → below → right → left) and the
 * `cdkConnectedOverlayPush` flag together guarantee the bubble stays on
 * screen even when the trigger is close to a viewport edge.
 */
@Component({
  selector: 'app-info-tip',
  imports: [MatIconModule, CdkConnectedOverlay, CdkOverlayOrigin],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './info-tip.html',
  styleUrl: './info-tip.css',
  host: {
    '(keydown.escape)': 'onEscape()',
  },
})
export class InfoTipComponent {
  private readonly overlay = inject(Overlay);
  private readonly destroyRef = inject(DestroyRef);

  readonly text = input.required<string>();
  readonly position = input<InfoTipPosition>('above');
  readonly ariaLabel = input<string>('More info');

  protected readonly open = signal(false);
  protected readonly scrollStrategy = this.overlay.scrollStrategies.reposition();

  /** Computed once per `position` input change. Returning a stable array
   *  reference keeps the CDK overlay from re-creating its position strategy
   *  on every change-detection pass. */
  protected readonly positions = computed<ConnectedPosition[]>(() => {
    const above: ConnectedPosition = {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -8,
      panelClass: 'app-info-tip-panel--above',
    };
    const below: ConnectedPosition = {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 8,
      panelClass: 'app-info-tip-panel--below',
    };
    const left: ConnectedPosition = {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -8,
      panelClass: 'app-info-tip-panel--left',
    };
    const right: ConnectedPosition = {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 8,
      panelClass: 'app-info-tip-panel--right',
    };
    switch (this.position()) {
      case 'below':
        return [below, above, right, left];
      case 'left':
        return [left, right, above, below];
      case 'right':
        return [right, left, above, below];
      case 'above':
      default:
        return [above, below, right, left];
    }
  });

  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.showTimer !== null) clearTimeout(this.showTimer);
      if (this.hideTimer !== null) clearTimeout(this.hideTimer);
    });
  }

  protected show(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (this.open() || this.showTimer !== null) return;
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      this.open.set(true);
    }, 150);
  }

  protected hide(): void {
    if (this.showTimer !== null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    if (!this.open() || this.hideTimer !== null) return;
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.open.set(false);
    }, 80);
  }

  protected onEscape(): void {
    if (this.open()) {
      this.open.set(false);
    }
  }
}
